"use client"
import Link from "next/link"
import { useReadContract } from "wagmi"
import { LoomlockLogoFull, LoomlockLogo } from "../LoomlockLogo"
import {
  Button,
  NumberLoader,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@abraxas/ui/ui"
import { cn, formatAddress } from "@abraxas/ui/lib"
import { ConnectButton as RBConnectButton } from "@rainbow-me/rainbowkit"
import { blessnet, blessnetSepolia, useVaults } from "@abraxas/ui/web3"
import { AnimatePresence, motion } from "motion/react"

import { ChevronDown, InfoIcon, Menu, X } from "lucide-react"
import { ABIBlessedPoP, config } from "@repo/loomlock/client"
import { useState, useEffect } from "react"
import { IS_PROD } from "@abraxas/ui/is-prod"
import { Delegates } from "./Delegates"
import { usePathname } from "next/navigation"
import { ErrorBoundary } from "@sentry/nextjs"
import { MARKETPLACE_ENABLED } from "../feature-flags"
import { useLoomlockCouponBalance } from "../loomlock-coupons"
import {
  LOOMLOCK_COUPONS_METADATA,
  LOOMLOCK_COUPONS,
} from "../loomlock-coupons/types"

const LocksBalanceDropdown = ({
  data,
}: {
  data: ReturnType<typeof useLoomlockCouponBalance>["data"]
}) => {
  return (
    <div className="w-screen max-w-xs flex flex-col gap-4">
      <div className="font-bold">My balances</div>
      <ul className="flex flex-col divide-y divide-brand-blue-400">
        {LOOMLOCK_COUPONS.map((tokenId) => {
          const token = data?.tokens[tokenId]
          const metadata = LOOMLOCK_COUPONS_METADATA[tokenId]
          return (
            <div key={tokenId} className="flex justify-between w-full py-4">
              {metadata.name}
              <span>
                <NumberLoader value={token?.balance} />
              </span>
            </div>
          )
        })}
      </ul>
      <Button href="/account" color="white" className="w-full">
        My account
      </Button>
    </div>
  )
}

const LocksBalance = () => {
  const { effectiveAddress } = useVaults()
  const { data } = useLoomlockCouponBalance({
    address: effectiveAddress,
  })
  const balance = data?.tokens[1]?.balance

  if (!effectiveAddress) {
    return null
  }

  return (
    <ErrorBoundary>
      <Popover openOnHover>
        <PopoverTrigger asChild>
          <div className="px-4 whitespace-nowrap cursor-pointer flex items-center gap-2">
            <NumberLoader value={balance} /> $LOCKS
            <ChevronDown className="transition-all" size={16} />
          </div>
        </PopoverTrigger>
        <PopoverContent align="end">
          <LocksBalanceDropdown data={data} />
        </PopoverContent>
      </Popover>
    </ErrorBoundary>
  )
}

export const useBlessedPops = () => {
  const { address, onBehalfOf } = useVaults()
  const chainId = IS_PROD ? blessnet.id : blessnetSepolia.id

  return useReadContract({
    address: config[String(chainId)]!.contracts.blessedPoP,
    abi: ABIBlessedPoP,
    functionName: "balanceOf",
    args: [(onBehalfOf || address) as `0x${string}`, BigInt(1)],
    chainId,
    query: {
      enabled: Boolean(address),
    },
  })
}

const AccountDropdown = () => {
  const { data } = useBlessedPops()
  return (
    <div className="w-screen max-w-xs flex flex-col gap-4">
      <div className="font-bold">my account</div>
      <div className="flex justify-between w-full">
        Blessed PoPs
        <span>
          <NumberLoader value={data} />
        </span>
      </div>
      <Link
        href="/blessed-pops"
        className="text-xs text-yellow flex items-center gap-2 group hover:text-yellow"
      >
        <InfoIcon size={16} />
        <span className="group-hover:no-underline">learn more about PoPs</span>
      </Link>
      <hr className="hr sm text-white" />
      <div>
        <div className="font-bold">delegated accounts</div>
        <span className="text-yellow text-xs">
          Choose which delegated account to represent
        </span>
      </div>

      <Delegates />
      <a
        href="https://docs.bless.net/blessnet-apps/modular-permissions"
        target="blank"
        className="text-xs text-yellow flex items-center gap-2 group hover:text-yellow"
      >
        <InfoIcon size={16} />
        <span className="group-hover:no-underline">
          learn more about delegations
        </span>
      </a>
      <Link
        href="/account/locks/approvals"
        className="text-xs text-yellow flex items-center gap-2 group hover:text-yellow"
      >
        <InfoIcon size={16} />
        <span className="group-hover:no-underline">
          manage $LOCKS approvals
        </span>
      </Link>
    </div>
  )
}

const ConnectButton = () => {
  const { onBehalfOf } = useVaults()
  return (
    <RBConnectButton.Custom>
      {({
        account,
        openConnectModal,
        openAccountModal,
        authenticationStatus,
        mounted,
      }) => {
        const ready = mounted && authenticationStatus !== "loading"
        if (!account) {
          return (
            <Button
              loading={!ready}
              onClick={openConnectModal}
              className="translate-x-5"
            >
              connect
            </Button>
          )
        }

        return (
          <Popover openOnHover>
            <PopoverTrigger asChild>
              <div className="flex items-center gap-2 text-white border-brand-blue-400 cursor-pointer">
                <div className="flex items-end flex-col  min-w-[12ch]">
                  <AnimatePresence>
                    {onBehalfOf && (
                      <motion.div
                        initial={{
                          maxHeight: 0,
                          opacity: 0,
                        }}
                        animate={{
                          maxHeight: "1.2rem",
                          opacity: 1,
                        }}
                        exit={{
                          maxHeight: 0,
                          opacity: 0,
                          y: -15,
                        }}
                        transition={{
                          duration: 0.15,
                          ease: "easeOut",
                        }}
                      >
                        {formatAddress(onBehalfOf)}
                      </motion.div>
                    )}
                  </AnimatePresence>
                  <button
                    type="button"
                    className={cn(
                      onBehalfOf && "text-[10px]",
                      "transition-all",
                    )}
                    onClick={openAccountModal}
                  >
                    {onBehalfOf && "via "}
                    {account?.ensName || formatAddress(account?.address!)}
                  </button>
                </div>
                <ChevronDown className="transition-all" size={22} />
              </div>
            </PopoverTrigger>
            <PopoverContent align="end">
              <AccountDropdown />
            </PopoverContent>
          </Popover>
        )
      }}
    </RBConnectButton.Custom>
  )
}

const menuItems = [
  // { href: "/votes/submit-your-design", label: "Contest" },
  { href: "/events", label: "Events" },
  { href: "/gachabox", label: "Gachabox" },
  MARKETPLACE_ENABLED && { href: "/marketplace", label: "Marketplace" },
  { href: "/shop", label: "Shop" },
].filter(Boolean) as unknown as { href: string; label: string }[]
export const Header = ({ className }: { className?: string }) => {
  const path = usePathname()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = "unset"
    }
    return () => {
      document.body.style.overflow = "unset"
    }
  }, [mobileMenuOpen])

  return (
    <>
      <div
        className="container mx-auto sticky top-0 pt-6 pb-5 z-1000 left-0 right-0"
        id="header-wrapper"
        suppressHydrationWarning
      >
        <header
          className={cn(
            "rounded-full",
            "px-8 md:-mx-8",
            "transition-all",
            mobileMenuOpen ? "bg-transparent!" : "bg-white/5! backdrop-blur-lg",
            className,
          )}
        >
          <div className="px-0 flex shrink-0 justify-between items-center gap-4 py-1.5">
            <Link
              href="/#the-lock"
              className="text-white hover:text-brand-ultralight shrink-0"
            >
              <img
                src="/images/logos/logo-white.svg"
                alt="Loomlock"
                className="h-10 hidden md:block"
              />
              <LoomlockLogo className="h-10 md:hidden" />
            </Link>

            <div
              className={cn(
                "shrink px-8 rounded-full hidden md:inline-flex justify-around bg-white/20 gap-8 lg:gap-16",
                // advent
                // "pr-24 relative gap-10",
              )}
            >
              {menuItems.map(({ href, label }) => (
                <ErrorBoundary key={href}>
                  <Link
                    className={cn(
                      "text-body-m text-white/80  py-2",
                      "active:scale-95 hover:scale-105 transition-all",
                      path === href && "text-white font-semibold",
                    )}
                    href={href}
                  >
                    {label}
                  </Link>
                </ErrorBoundary>
              ))}
              {/* <Link
                href="/advent"
                className="w-20 absolute -bottom-1 right-1 hover:scale-105 transition-all"
                style={{
                  transformOrigin: "bottom right",
                }}
              >
                <img
                  src="/images/advent/banner-santa-locky.png"
                  alt="Advent mascot"
                  className="object-contain object-bottom h-full "
                />
              </Link> */}
            </div>

            <div className="hidden md:flex items-center gap-2 divide-x">
              <LocksBalance />
              <ConnectButton />
            </div>

            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-white p-2"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </header>
      </div>
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, x: "100%" }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: "100%" }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="fixed inset-0 z-999 bg-brand-blue-500/95 backdrop-blur-lg md:hidden"
          >
            <div className="h-full flex flex-col p-8">
              <nav className="flex-1 flex flex-col items-center justify-center gap-8">
                {menuItems.map(({ href, label }) => (
                  <ErrorBoundary key={href}>
                    <Link
                      className={cn(
                        "text-2xl text-white/80 font-semibold",
                        "active:scale-95 hover:scale-105 transition-all",
                        path === href && "text-white",
                      )}
                      href={href}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {label}
                    </Link>
                  </ErrorBoundary>
                ))}
                {/* <Link
                  className={cn(
                    "text-2xl text-white/80 font-semibold",
                    "active:scale-95 hover:scale-105 transition-all",
                    path === "/advent" && "text-white",
                  )}
                  href="/advent"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Advent
                </Link> */}
              </nav>

              <div className="flex flex-col items-center gap-4 pb-8">
                <LocksBalance />
                <ConnectButton />
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  )
}
